import React from 'react';
import {
  Reservation,
  ReservationSummary,
  PaginationResult,
  Table,
} from '../../dashboard/types';
import { Crown } from 'lucide-react';
import { getStatusBadge } from '../../ui/ReservationStatusBadge';

const formatCreatedAt = (value: string) => new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Africa/Dar_es_Salaam',
}).format(new Date(value));

const configuredAdvanceMinutes = Number(process.env.REACT_APP_RESERVATION_MIN_ADVANCE_MINUTES ?? 60);
const minimumAdvanceMinutes = Number.isFinite(configuredAdvanceMinutes) ? Math.max(0, configuredAdvanceMinutes) : 60;
const configuredOpeningTime = String(process.env.REACT_APP_RESERVATION_EARLIEST_START_TIME ?? '12:00');
const earliestDailyStart = /^([01]\d|2[0-3]):[0-5]\d$/.test(configuredOpeningTime) ? configuredOpeningTime : '12:00';
const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const localTime = (date: Date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const startLabel = (reservation: Reservation, now: number) => {
  const start = new Date(`${reservation.date}T${reservation.timeStart}`).getTime();
  const difference = start - now;
  if (difference <= 0) return `Started at ${reservation.timeStart}`;

  const minutes = Math.max(1, Math.ceil(difference / 60000));
  if (minutes === 60) return 'Starts in 1 hour';
  return `Starts in ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

const durationLabel = (target: number, prefix: string, now: number) => {
  const minutes = Math.max(1, Math.ceil((target - now) / 60000));
  return `${prefix} in ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

interface ReservationsTabProps {
  summary: ReservationSummary | null;
  trendMax: number;
  nextHourReservations: Reservation[];
  attentionReservations: Reservation[];
  reservations: PaginationResult<Reservation> | null;
  page: number;
  onSelectReservation: (reservationId: string) => void;
  onPageChange: (page: number) => void;
  canCreate: boolean;
  tables: Table[];
  onCreate: (payload: Record<string, unknown>) => Promise<boolean>;
  bookingEnabled: boolean;
  canToggleBookings: boolean;
  isUpdatingBookingStatus: boolean;
  onToggleBookings: (enabled: boolean) => Promise<void>;
}

export const ReservationsTab: React.FC<ReservationsTabProps> = ({
  summary,
  trendMax,
  nextHourReservations,
  attentionReservations,
  reservations,
  page,
  onSelectReservation,
  onPageChange,
  canCreate,
  tables,
  onCreate,
  bookingEnabled,
  canToggleBookings,
  isUpdatingBookingStatus,
  onToggleBookings,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  const [createForm, setCreateForm] = React.useState({customerName: '', customerEmail: '', customerPhone: '', date: '', startTime: '', endTime: '', numberOfGuests: 2, sectionId: '', tableId: '', additionalTableIds: [] as string[], notes: ''});
  const sections = Array.from(new Map(tables.filter((table) => table.section).map((table) => [table.sectionId, table.section!])).values());
  const selectableTables = tables.filter((table) => table.sectionId === createForm.sectionId && !table.reserved && (createForm.numberOfGuests > 7 || table.capacity >= createForm.numberOfGuests));
  const selectedCapacity = selectableTables.filter((table) => table.id === createForm.tableId || createForm.additionalTableIds.includes(table.id)).reduce((total, table) => total + table.capacity, 0);
  const earliestStart = new Date(Date.now() + minimumAdvanceMinutes * 60 * 1000);
  const earliestDate = localDate(earliestStart);
  const minimumStartTime = createForm.date === earliestDate
    ? [earliestDailyStart, localTime(earliestStart)].sort().slice(-1)[0]
    : earliestDailyStart;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleReservations = (reservations?.items || []).filter((reservation) => {
    if (!normalizedSearch) return true;

    return [
      reservation.customerName,
      reservation.customerEmail,
      reservation.customerPhone,
      reservation.date,
      reservation.timeStart,
      reservation.timeEnd,
      reservation.numberOfGuests,
      reservation.status,
      reservation.createdAt,
      reservation.isVip ? 'vip' : 'regular',
    ].some((value) => String(value ?? '').toLowerCase().includes(normalizedSearch));
  }).sort((left, right) => {
    const leftStart = new Date(`${left.date}T${left.timeStart}`).getTime();
    const rightStart = new Date(`${right.date}T${right.timeStart}`).getTime();
    return leftStart - rightStart || new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
  });

  React.useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const endingSoonReservations = attentionReservations.filter((reservation) => {
    if (!reservation.timeEnd || !['booked', 'confirmed', 'checked_in'].includes(reservation.status)) return false;
    const end = new Date(`${reservation.date}T${reservation.timeEnd}`).getTime();
    return end > currentTime && end <= currentTime + 30 * 60 * 1000;
  }).sort((left, right) => new Date(`${left.date}T${left.timeEnd}`).getTime() - new Date(`${right.date}T${right.timeEnd}`).getTime());

  return (
    <>
      {canToggleBookings && <section className='reservation-create-panel'>
        <div className='reservation-create-heading'>
          <div><p className='eyebrow'>Website availability</p><h2>Online Bookings</h2><p>{bookingEnabled ? 'Customers can currently book through the website.' : 'Website bookings are currently paused.'}</p></div>
          <label className='booking-toggle'>
            <span>{bookingEnabled ? 'Enabled' : 'Paused'}</span>
            <input className='booking-toggle-input' type='checkbox' role='switch' aria-label='Enable website bookings' checked={bookingEnabled} disabled={isUpdatingBookingStatus} onChange={(event) => void onToggleBookings(event.target.checked)} />
            <span className='booking-toggle-track' aria-hidden='true'><span /></span>
          </label>
        </div>
      </section>}
      {canCreate && <section className='reservation-create-panel'>
        <div className='reservation-create-heading'><div><p className='eyebrow'>Staff booking</p><h2>Add Reservation</h2></div><button type='button' className='primary-action' onClick={() => setShowCreateForm((value) => !value)}>{showCreateForm ? 'Cancel' : 'Add Reservation'}</button></div>
        {showCreateForm && <form className='reservation-create-form' onSubmit={async (event) => {
          event.preventDefault();
          const table = tables.find((item) => item.id === createForm.tableId);
          setIsCreating(true);
          const saved = await onCreate({...createForm, isVip: Boolean(table?.isVip)});
          setIsCreating(false);
          if (saved) {
            setCreateForm({customerName: '', customerEmail: '', customerPhone: '', date: '', startTime: '', endTime: '', numberOfGuests: 2, sectionId: '', tableId: '', additionalTableIds: [], notes: ''});
            setShowCreateForm(false);
          }
        }}>
          <label>Customer name<input value={createForm.customerName} onChange={(event) => setCreateForm({...createForm, customerName: event.target.value})} required /></label>
          <label>Email<input type='email' value={createForm.customerEmail} onChange={(event) => setCreateForm({...createForm, customerEmail: event.target.value})} required /></label>
          <label>Phone<input type='tel' value={createForm.customerPhone} onChange={(event) => setCreateForm({...createForm, customerPhone: event.target.value})} required /></label>
          <label>Date<input type='date' min={earliestDate} value={createForm.date} onChange={(event) => setCreateForm({...createForm, date: event.target.value})} required /></label>
          <label>Start time<input type='time' min={minimumStartTime} value={createForm.startTime} onChange={(event) => setCreateForm({...createForm, startTime: event.target.value})} required /><small>Starts from {earliestDailyStart}; book at least {minimumAdvanceMinutes} minutes ahead.</small></label>
          <label>End time<input type='time' value={createForm.endTime} onChange={(event) => setCreateForm({...createForm, endTime: event.target.value})} required /></label>
          <label>Guests<input type='number' min='1' max='100' value={createForm.numberOfGuests} onChange={(event) => setCreateForm({...createForm, numberOfGuests: Number(event.target.value), tableId: '', additionalTableIds: []})} required /></label>
          <label>Section<select value={createForm.sectionId} onChange={(event) => setCreateForm({...createForm, sectionId: event.target.value, tableId: '', additionalTableIds: []})} required><option value=''>Select section</option>{sections.map((section) => <option key={section.id} value={section.id}>{section.name}</option>)}</select></label>
          <label>Primary table<select value={createForm.tableId} onChange={(event) => setCreateForm({...createForm, tableId: event.target.value, additionalTableIds: createForm.additionalTableIds.filter((id) => id !== event.target.value)})} required disabled={!createForm.sectionId}><option value=''>Select available table</option>{selectableTables.map((table) => <option key={table.id} value={table.id}>{table.description || `Table ${table.tableNumber}`} · {table.capacity} seats</option>)}</select></label>
          {createForm.numberOfGuests > 7 && <fieldset className='reservation-create-notes'>
            <legend>Additional tables</legend>
            <p className='text-sm'>Selected capacity: {selectedCapacity} of {createForm.numberOfGuests} guests</p>
            <div className='flex flex-wrap gap-2'>{selectableTables.filter((table) => table.id !== createForm.tableId).map((table) => <label key={table.id} className='flex items-center gap-2'><input type='checkbox' checked={createForm.additionalTableIds.includes(table.id)} onChange={(event) => setCreateForm({...createForm, additionalTableIds: event.target.checked ? [...createForm.additionalTableIds, table.id] : createForm.additionalTableIds.filter((id) => id !== table.id)})} />{table.description || `Table ${table.tableNumber}`} · {table.capacity} seats</label>)}</div>
          </fieldset>}
          <label className='reservation-create-notes'>Notes<textarea value={createForm.notes} onChange={(event) => setCreateForm({...createForm, notes: event.target.value})} maxLength={500} /></label>
          <button type='submit' className='primary-action' disabled={isCreating}>{isCreating ? 'Saving…' : 'Create Reservation'}</button>
        </form>}
      </section>}
      <section className='metric-row'>
        <article className='metric-card'>
          <p>Total this week</p>
          <strong>{summary?.thisWeekCount ?? 0}</strong>
        </article>

        <article className='metric-card'>
          <p>Available Tables</p>
          <div className='w-full mt flex justify-between items-center gap-2'>
            <strong className='text-sm'>
              VIP - {summary?.availableVipTables ?? 0}
            </strong>
            <strong className='text-sm'>
              Regular - {summary?.availableRegularTables ?? 0}
            </strong>
          </div>
        </article>
        <article className='metric-card'>
          <p>Paid Reservations</p>
          <strong>{summary?.paidReservations ?? 0}</strong>
        </article>
        <article className='metric-card'>
          <p>Today's Reservations</p>
          <strong>{summary?.todayCount ?? 0}</strong>
        </article>
      </section>

      <section className='attention-row'>
        <article className='panel-card mini'>
          <header>
            <h2>Starting Soon</h2>
          </header>
          <p>Starting within one hour before or after now: {nextHourReservations.length}</p>
          {nextHourReservations.length > 0 ? (
            <ul className='service-feed'>
              {nextHourReservations.slice(0, 5).map((reservation) => (
                <li
                  key={reservation.id}
                  className='service-item'>
                  <span className='service-name'>
                    {reservation.customerName}
                  </span>
                  <span className='service-meta'>
                    {startLabel(reservation, currentTime)}{reservation.timeEnd ? ` · ends ${reservation.timeEnd}` : ''} |{' '}
                    {reservation.numberOfGuests} guests
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No reservations due in the next hour.</p>
          )}
        </article>
        <article className='panel-card mini'>
          <header>
            <h2>Ending Soon</h2>
          </header>
          <p>Ending within the next 30 minutes: {endingSoonReservations.length}</p>
          {endingSoonReservations.length > 0 ? (
            <ul className='service-feed'>
              {endingSoonReservations.slice(0, 5).map((reservation) => {
                const end = new Date(`${reservation.date}T${reservation.timeEnd}`).getTime();
                return <li key={reservation.id} className='service-item'>
                  <span className='service-name'>{reservation.customerName}</span>
                  <span className='service-meta'>{durationLabel(end, 'Ends', currentTime)} · {reservation.timeEnd} | {reservation.numberOfGuests} guests</span>
                </li>;
              })}
            </ul>
          ) : (
            <p>No reservations ending in the next half hour.</p>
          )}
        </article>
      </section>

      <section className='table-card'>
        <header className='reservation-table-header'>
          <h2>Reservations</h2>
          <div className='reservation-search'>
            <label htmlFor='reservation-search'>Search reservations</label>
            <div className='reservation-search-input'>
              <input
                id='reservation-search'
                type='search'
                placeholder='Name, email, date, status…'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button type='button' onClick={() => setSearchTerm('')}>
                  Clear
                </button>
              )}
            </div>
            <span>{visibleReservations.length} {normalizedSearch ? 'matches' : 'reservations'}</span>
          </div>
        </header>
        <div className='table-wrap'>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                {/* <th>Email</th> */}
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Guests</th>
                <th>Status</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleReservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td>{reservation.customerName}</td>
                  {/* <td>{reservation.customerEmail}</td> */}
                  <td>{reservation.date}</td>
                  <td>{reservation.timeStart}</td>
                  <td>{reservation.timeEnd || '—'}</td>
                  <td>{reservation.numberOfGuests}</td>
                  <td>
                    {getStatusBadge(reservation.status)}
                  </td>
                  <td>{formatCreatedAt(reservation.createdAt)}</td>
                  <td className='flex justify-between items-center '>
                    <button onClick={() => onSelectReservation(reservation.id)}  className='cursor-pointer px-3 py-2 rounded-lg'>
                      View
                    </button>
                    {reservation.isVip && (
                      <span className='px-2 text-sm rounded-lg text-yellow-500'>
                        <Crown size={24} />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {visibleReservations.length === 0 && (
                <tr>
                  <td colSpan={8} className='empty-row'>
                    {normalizedSearch ? 'No reservations match your search.' : 'No reservations found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className='pager'>
          <button  className='cursor-pointer px-3 py-2 rounded-lg'
            disabled={!reservations?.hasPreviousPage}
            onClick={() => onPageChange(Math.max(1, page - 1))}>
            Previous
          </button>
          <span>
            Page {reservations?.currentPage || 1} of{' '}
            {reservations?.totalPages || 1}
          </span>
          <button className='cursor-pointer px-3 py-2 rounded-lg'
            disabled={!reservations?.hasNextPage}
            onClick={() => onPageChange(page + 1)}>
            Next
          </button>
        </div>
      </section>

      <section className='panel-card popular-days-card'>
        <header>
          <h2>Most Popular Days</h2>
        </header>
        <div className='trend-chart'>
          {(summary?.trendLast7Days || []).map((point) => (
            <div className='trend-day' key={point.date}>
              <div className='trend-bars'>
                <div className='bar total' style={{height: `${(point.totalCount / trendMax) * 100}%`}} title={`Total: ${point.totalCount}`} />
                <div className='bar cancelled' style={{height: `${(point.cancelledCount / trendMax) * 100}%`}} title={`Cancelled: ${point.cancelledCount}`} />
              </div>
              <p>{new Date(point.date).toLocaleDateString('en-US', {weekday: 'short'})}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};
